<template lang="pug">
  #account-edit(v-cloak)
    .box
      .box-header.with-border.register-header
        .title
          span ユーザー情報修正
      .box-body.register-body
        FormLabel(key-name="ユーザー種別" :value="_roleName")
        FormLabel(key-name="ユーザーID（ログインID）" :value="_loginId" style="margin-bottom: 0;")
        span.warning-message(style="margin: 0 20px 1rem 20px;") ユーザーIDはメールアドレスのみ使用できます。ユーザーIDの変更をご希望の場合は、お問い合わせから弊社にご連絡ください。
        FormInput(v-model="name" key-name="氏名" name="user-name" type="text" placeholder="氏名" :maxlength="32"
          :validation-rules="{ required: true, max: 32 }" required)
        FormInput(v-model="kana" key-name="氏名（かな）" name="user-kana" type="text" placeholder="氏名（かな）" :maxlength="32"
          :validation-rules="{ required: true, max: 32, hiragana: true }" required)
        FormInput(v-model="tel" key-name="電話番号" name="user-tel" type="text" placeholder="電話番号" :maxlength="13"
          :validation-rules="{ required: true, max: 13, tel: true }" :styles="{ width: '340px' }" required)
        FormInput(v-model="mail" key-name="メールアドレス" name="mail" type="text" placeholder="半角小文字英数で入力してください" :maxlength="100"
          :validation-rules="{ required: true, max: 100, email: true, non_uppercase:true, equal_value: confirmMail }" required)
          template
            .warning-message.d-flex.flex-column
              span メールアドレスはユーザーIDとは別のメールアドレスを設定できます。
              span メールアドレスは弊社からのお知らせ・お問い合わせ等でご連絡させていただくメールアドレスになります。
        FormInput(v-model="confirmMail" name="confirm-mail" key-name="メールアドレス（確認）" type="text" placeholder="確認のため同じ内容を入力してください"
          :maxlength="100" :validation-rules="{ required: true, max: 100, email: true, non_uppercase:true }" required)
        FormInputPassword(v-model="oldPassword" key-name="現在のパスワード" name="old-password" placeholder="現在のパスワード" :maxlength="100"
          :validation-rules="{ required: true, half_alpha_num:true, max: 100 }" required)
          // 現在のパスワードが異なる場合のエラーメッセージはslotで渡す
          .error-messages(v-if="errors.has('unauthenticated')")
            label.error-message(v-for="error in errors.collect('unauthenticated')") {{ error }}
        FormInputPassword(v-model="newPassword" key-name="新しいパスワード" name="new-password" placeholder="半角英数字8文字以上で設定してください" :maxlength="100"
          :validation-rules="{ half_alpha_num:true, min: 8, max: 100, equal_value: confirmNewPassword }")
        FormInputPassword(v-model="confirmNewPassword" key-name="新しいパスワード（確認）" name="confirm-new-password" placeholder="確認のため同じ内容を入力してください"
          :maxlength="100" :validation-rules="{ half_alpha_num:true, min: 8, max: 100 }")

        .register-row(v-show="errorMessage")
          .register-group
            .register-value
              .error-message {{ errorMessage }}

      .box-footer.with-border.register-footer
        ValidationAlert(v-model="showValidationAlert")
        .buttons
          button.btn.update-btn(@click="showConfirmUpdateModal" type="button") 更新
          button.btn.cancel-btn(@click="gotoBeforePage" type="button") キャンセル

    OkCancelModal(v-model="showUpdateModal" title="ユーザー情報を更新しますか？" @ok="updateUser")
    OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoBeforePage")

    PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import CustomValidate from "@/commons/utils/CustomValidate";

import { Account, Role } from "@/commons/services/Authorization";
import AccountsService, { UpdateAccountsPatchModel, UpdateType } from "@/commons/services/Accounts";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputPassword from "@/pc/components/forms/FormInputPassword.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import SystemError from "@/commons/models/SystemError";

/**
 * ユーザー情報編集画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormInput,
    FormInputPassword,
    FormLabel
  },
  $_veeValidate: { validator: "new" }
})
export default class AccountEdit extends Vue {
  /** ユーザーサービス */
  @Inject("AccountsService") accountsService!: AccountsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** ユーザー種別 */
  role: Role | null = null;
  /** 名前 */
  name: string = "";
  /** 名前かな */
  kana: string = "";
  /** 電話番号 */
  tel: string = "";
  /** メールアドレス */
  mail: string = "";
  /** メールアドレス(確認) */
  confirmMail: string = "";
  /** 旧パスワード */
  oldPassword: string = "";
  /** 新パスワード */
  newPassword: string = "";
  /** 新パスワード(確認) */
  confirmNewPassword: string = "";
  /** エラーメッセージ */
  errorMessage: string = "";

  get _roleName(): string {
    if (this.role == null) {
      return "";
    }

    let roleName: string = "";
    switch (this.role) {
      case Role.KM_USER:
        roleName = "サービス管理者ユーザー";
        break;
      case Role.ADMIN_USER:
        roleName = "管理者ユーザー";
        break;
      case Role.STANDARD_USER:
        roleName = "一般ユーザー";
        break;
    }

    return roleName;
  }

  get _loginId(): string {
    return userStore.user.userId;
  }

  /**
   * vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);

    return;
  }

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    await this.createView();

    this.isBusy = false;

    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    // ユーザー情報を取得する
    const account: Account = await this.getAccounts();

    // 取得したアカウント情報がログイン時に取得した情報より新しければ更新する
    if (userStore.loginUser.updated < account.updated) {
      userStore.updateLoginUser({ loginUser: account });
    }

    // 現在のユーザー情報を入力する
    this.role = userStore.loginUser.role;
    this.name = userStore.loginUser.name;
    this.kana = userStore.loginUser.kana;
    this.tel = userStore.loginUser.tel;
    this.mail = userStore.loginUser.mail;
    this.confirmMail = userStore.loginUser.mail;

    return;
  }

  /**
   * ### アカウント情報を取得する
   */
  public async getAccounts(): Promise<Account> {
    const account: Account = await this.accountsService.post(userStore);
    return account;
  }

  /**
   * 更新確認モーダルを表示する
   * 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    // 現在のパスワードの認証エラーメッセージを初期化する
    this.$validator.errors.remove("unauthenticated");

    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ユーザー更新
   */
  public async updateUser(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      // ユーザー更新
      const user: UpdateAccountsPatchModel = this.createUpdateParam();
      const updatedUser: any = await this.accountsService.patch(userStore, user);

      // 保持しているログインユーザー情報を更新する
      const loginUser: Account = {
        userId: user.userId,
        facilityId: userStore.loginUser.facilityId,
        role: userStore.loginUser.role,
        name: this.name,
        kana: this.kana,
        tel: this.tel,
        mail: this.mail,
        latestRequestDatetime: userStore.loginUser.latestRequestDatetime,
        receiveNotification: userStore.loginUser.receiveNotification,
        started: userStore.loginUser.started,
        confirmedNotificationIds: userStore.loginUser.confirmedNotificationIds,
        permissions: userStore.loginUser.permissions,
        deleteFlag: userStore.loginUser.deleteFlag,
        updated: updatedUser.updated,
        created: userStore.loginUser.created
      };
      userStore.updateLoginUser({ loginUser: loginUser });

      this.showUpdateSuccessModal = true;
    } catch (error) {
      // 現在のパスワードに誤りがあり、認証で失敗した場合は422が返却される
      // 422が返却された場合はエラーメッセージを表示する
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 422) {
        this.$validator.errors.add({ field: "unauthenticated", msg: "現在のパスワードに誤りがあるためユーザー情報を変更できません" });
      }
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      else if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ユーザー更新パラメータ作成
   */
  public createUpdateParam(): UpdateAccountsPatchModel {
    const user: UpdateAccountsPatchModel = {
      userId: userStore.loginUser.userId,
      updateType: UpdateType.MINE,
      // 新しいパスワードは必須入力ではないので、新しいパスワードが空の場合は古いパスワードで更新する
      password: this.newPassword ? this.newPassword : this.oldPassword,
      oldPassword: this.oldPassword,
      name: this.$stringUtil.convertYen(this.name),
      kana: this.kana,
      tel: this.tel,
      mail: this.$stringUtil.convertYen(this.mail),
      updated: userStore.loginUser.updated
    };

    return user;
  }

  /**
   * 1つ前の画面に遷移する
   */
  public gotoBeforePage(): void {
    this.$router.go(-1);

    return;
  }
}
</script>

<style lang="scss" scoped>
#account-edit {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  #user-name,
  #user-kana,
  #mail,
  #confirm-mail,
  #old-password,
  #new-passsword,
  #confirm-new-passsword {
    min-width: 580px;
    width: 580px;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
